import {
  CreateDictionaryRecordDto,
  DataBlockVariant,
  CreateDataBlockTypeDto,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field } from '../common/Field';
import { SubmitButton } from '../common/ui-kit/buttons/SubmitButton';
import { Input } from '../common/ui-kit/inputs/Input';
import { FormComponentProps } from '../global-forms/GlobalForms.types';
import { useCallback } from 'react';
import { QueryFactory } from '@lmt/shared-types';
import { useQueryClient } from '@tanstack/react-query';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';

type CreateDataBlockTypeProps = FormComponentProps & {
  variant: DataBlockVariant;
  onSuccess?: (dataBlockTypeId: number) => void;
};
const nameof = nameofFactory<CreateDictionaryRecordDto>();

export const CreateDataBlockType: FC<CreateDataBlockTypeProps> = (props) => {
  const { formType, formId, tryCompleteForm } = props;

  const i18next = useTranslation();
  const modals = useModal();
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState, setError } =
    useForm<CreateDictionaryRecordDto>({
      shouldFocusError: true,
      criteriaMode: 'all',
    });

  const formSubmit = useErrorHandler(
    useCallback(
      async (data: CreateDictionaryRecordDto) => {
        try {
          await tryCompleteForm(
            formType,
            formId,
            async () => {
              const dataBlockType: CreateDataBlockTypeDto = {
                value: data.value,
                variant: props.variant,
              };
              const createdDataBlockType =
                await ClientFactory.DataBlockTypeClient.create(dataBlockType);
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.DataBlockTypeQuery.getDataBlockTypesForEditingQueryKey(),
              });
              await queryClient.refetchQueries({
                queryKey: QueryFactory.DataBlockTypeQuery.getQueryKey(),
              });
              setTimeout(() => {
                props.onSuccess?.(createdDataBlockType.id);
              }, 50);
            },
            i18next.t('data_blocks.loading'),
          );
        } catch (err) {
          const errorDetails = handleSubmitFormError<CreateDictionaryRecordDto>(
            err,
            setError,
          );
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [formType, formId, tryCompleteForm],
    ),
    setError,
  );

  return (
    <form onSubmit={handleSubmit(formSubmit.handler)}>
      <div className={styles.dataContainer}>
        <Field title={i18next.t('data_blocks.types.name')}>
          <Input
            {...register(nameof('value'), requiredRule())}
            errorText={formState.errors.value?.message}
            autoFocus={true}
          />
        </Field>
      </div>
      <div className={styles.submitContainer}>
        <SubmitButton
          submitting={formState.isSubmitting}
          className={styles.submit}
          title={i18next.t('data_blocks.create_button')}
        />
      </div>
    </form>
  );
};
